import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { NotificationService } from '../core/services/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  LoadDashboardCheckSheetAssignmentStatus,
  LoadDashboardCps,
  LoadDashboardMcps,
  LoadPrePunches,
  LoadUnacceptedPunches,
  LoadUserPunches,
  LoadUserSignatureAssignments,
} from '@completion/actions';
import { ApplicationMode, ProjectConfigs, ResourceStatus, ResourceType, RoleState } from '@completion/enums';
import {
  getCurrentProject,
  getDashboardCheckSheetAssignmentStatus,
  getDashBoardCps,
  getDashBoardMcps,
  getOfflineState,
  getResourceState,
  getRole,
} from '@completion/selectors';
import { ContextTool, ContextToolbarService, TooledComponent } from '../context-toolbar/context-toolbar.service';
import { DashboardPackage, Project, ProjectAccessRoles } from '@completion/models';
import { ProjectConfigService } from '../core/services/project-config.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy, TooledComponent {
  readonly tools: ContextTool[];
  displayedColumns: string[] = ['description', 'status'];
  filterOnCp: boolean;
  projectProgress: string;
  mcpDashboardPackages: DashboardPackage[];
  cpDashboardPackages: DashboardPackage[];

  isOffline: boolean = false;
  accepted = 'accepted';
  assignmentCount = 0;
  badgeHidden = true;
  roleState: RoleState = RoleState.Unknown;
  RoleState = RoleState;
  pieChartData = null;
  isPrePunchEnabled = false;

  colorScheme = {
    domain: ['#d32f2f', '#FF9900', '#04804d', '#909191'], // Define your color palette here
  };
  public loading = true;

  private readonly destroy$: Subject<void> = new Subject();
  constructor(
    private readonly router: Router,
    private elRef: ElementRef,
    private readonly config: ProjectConfigService,
    private readonly store: Store<Project>,
    private readonly contextToolbarService: ContextToolbarService,
    private readonly toastr: NotificationService,
  ) {
    this.store
      .select(getRole)
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        if (role === ProjectAccessRoles.VENDOR) {
          this.roleState = RoleState.Vendor;
        } else {
          this.roleState = RoleState.NonVendor;
          this.loading = false;
        }
      });

    this.config.isInitialized$.pipe(takeUntil(this.destroy$)).subscribe((_isInitialized) => {
      this.isPrePunchEnabled = this.config.getConfig(ProjectConfigs.USE_PRE_PUNCH_IN_PROJECT);
    });

    this.tools = [
      // new ContextTool('Create CP', this.gotoCreateCp.bind(this), this)
    ];
    this.store
      .select(getResourceState, ResourceType.GetProjects)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (this.isOffline) {
          return;
        }
        this.projectProgress = state.status;
        switch (state.status) {
          case ResourceStatus.Idle:
            this.projectProgress = '';
            break;
          case ResourceStatus.InProgress:
            this.projectProgress = 'Fetching projects and tasks';
            this.toastr.info(this.projectProgress);
            break;
          case ResourceStatus.Success:
            this.projectProgress = 'Fetching projects and tasks success!';
            this.toastr.success(this.projectProgress + ' Start using the application by navigating in the left menu.');
            break;
          case ResourceStatus.Failure:
            this.projectProgress = 'Failed to fetch projects. ' + state.lastError.split(':')[0];
            break;
        }
      });

    this.store
      .select(getOfflineState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((offlineState) => (this.isOffline = offlineState === ApplicationMode.OFFLINE));
  }

  sortDataCp(sort: Sort) {
    const data = this.cpDashboardPackages.slice();
    if (!sort.active || sort.direction === '') {
      this.cpDashboardPackages = data;
      return;
    }

    this.cpDashboardPackages = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'description':
          return compare(a.description, b.description, isAsc);
        case 'status':
          return compare(a.status.completionState.toString(), b.status.completionState.toString(), isAsc);
        default:
          return 0;
      }
    });
  }

  sortDataMcp(sort: Sort) {
    const data = this.mcpDashboardPackages.slice();
    if (!sort.active || sort.direction === '') {
      this.mcpDashboardPackages = data;
      return;
    }

    this.mcpDashboardPackages = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'description':
          return compare(a.description, b.description, isAsc);
        case 'status':
          return compare(a.status.completionState.toString(), b.status.completionState.toString(), isAsc);
        default:
          return 0;
      }
    });
  }

  togglePackages(): void {
    this.filterOnCp = !this.filterOnCp;
  }

  async ngOnInit(): Promise<void> {
    this.contextToolbarService.addToolsFor(this);
    this.filterOnCp = true;
    this.renderDashboard();
  }

  addCount(count: number): void {
    if (count > 0) {
      this.toastr.info('You have ' + count + ' new assignments', 'New assignments');
      this.assignmentCount = count;

      this.badgeHidden = this.assignmentCount === 0;
    }
  }

  ngOnDestroy(): void {
    this.contextToolbarService.removeToolsFor(this);
    this.mcpDashboardPackages = [];
    this.cpDashboardPackages = [];

    this.destroy$.next();
    this.destroy$.complete();
  }

  renderDashboard(): void {
    if (this.isOffline) {
      return;
    }
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => {
        if (p !== null && !this.isOffline) {
          this.store.dispatch(new LoadUserPunches());
          this.store.dispatch(new LoadDashboardCps());
          this.store.dispatch(new LoadDashboardMcps());
          this.store.dispatch(new LoadDashboardCheckSheetAssignmentStatus());
          this.store.dispatch(new LoadUserSignatureAssignments());
          this.store.dispatch(new LoadUnacceptedPunches());
          if (this.isPrePunchEnabled) {
            this.store.dispatch(new LoadPrePunches());
          }

          this.store
            .select(getDashBoardCps)
            .pipe(takeUntil(this.destroy$))
            .subscribe((packages) => (this.cpDashboardPackages = packages));
          this.store
            .select(getDashBoardMcps)
            .pipe(takeUntil(this.destroy$))
            .subscribe((packages) => (this.mcpDashboardPackages = packages));
          this.store
            .select(getDashboardCheckSheetAssignmentStatus)
            .pipe(takeUntil(this.destroy$))
            .subscribe((statusSummary) => {
              if (statusSummary) {
                this.pieChartData = [
                  { name: 'PA', value: statusSummary.PACount },
                  { name: 'PB', value: statusSummary.PBCount },
                  { name: 'OK', value: statusSummary.OKCount },
                  { name: 'OS', value: statusSummary.OSCount },
                ];
              }
            });
        }
      });
  }
  getRouterLink(dashboardPackage: DashboardPackage) {
    if (dashboardPackage.type === 'CP') {
      return `/cp/${dashboardPackage.cpId}`;
    }
    return `/cp/${dashboardPackage.cpId}/mcp/${dashboardPackage.mcpId}`;
  }

  goToPackage(dashboardPackage: DashboardPackage) {
    this.router.navigate([this.getRouterLink(dashboardPackage)]);
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
